import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import styles from '../RewardSetup.module.scss';
import PeacockDataTable from '@/components/organisms/PeacockDataTable';
import {Icon, LinkText} from '@/components/atoms';
import LabelWithTooltip from '@/components/atoms/LabelWithTooltip/LabelWithTooltip';

import {ButtonIcon} from '@pepsico-ds/ui';
import {ConfirmationModal} from '@/components/molecules/ConfirmationModal/ConfirmationModal';
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert';
import {useDigitalCodesCard} from '@/hooks/useDigitalCodesCard';
import {FileUploadWithModal} from '@/components/organisms';
import {
  Constants,
  UPLOAD_TITLES,
  UPLOAD_ERROR_MESSAGES
} from '@/utils/constants/commonConstants';
import {useRewardCatalog} from '@/hooks/useRewardCatalog';
import {REWARD_STATUS} from '@/utils/constants/rewardsConstants';

const DigitalCodesCard = ({
  rewardStatus = REWARD_STATUS.DRAFT,
  onSaveDraft,
  inReview = false
}) => {
  const {
    pagedRecords,
    totalRecords,
    pageSize,
    pageIndex,
    setPageIndex,
    setSearchObj,
    handleSorting,
    onResetAll,
    onRowSelect,
    handleDownloadTemplate,
    handleBulkDeleteClick,
    handleSingleDeleteClick,
    confirmOpen,
    setConfirmOpen,
    handleConfirmDelete,
    loading,
    showDownloadEnabled,
    showUploadEnabled,
    canBulkDelete,
    rewardId,
    sortingObj,
    hasActiveFilters,
    tableKey
  } = useDigitalCodesCard({rewardStatus});

  // Reuse reward catalog upload flow for BulkDigitalCodes
  const {
    openUpload,
    setOpenUpload,
    uploading,
    setUploading,
    uploadProgress,
    uploadErrorMessage,
    formData,
    setFormData,
    handleBulkUpload,
    handleCloseNotification,
    handleCloseModal,
    customRules
  } = useRewardCatalog();

  const digitalCodesCustomRules = useMemo(() => {
    if (!customRules?.columns) return customRules;
    if (!rewardId) return customRules;

    return {
      ...customRules,
      columns: customRules.columns.map((column, index) =>
        index === 0 ? {...column, fixedValue: rewardId} : column
      )
    };
  }, [customRules, rewardId]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        label_of_column: 'Code ID',
        header_of_column: 'CODE ID',
        name_of_db_column: 'codeId'
      },
      {
        label_of_column: 'Customer ID',
        header_of_column: 'CUSTOMER ID',
        name_of_db_column: 'customerId',
        cellData: ({row: {original} = {}}) => {
          return (
            <LinkText
              text={original?.customerId}
              id={original?.customerId + ' ' + original?.id || original?.uuid}
            />
          );
        },
        linkHandler: () => {},
        linkLabel: (row) => row.customerId || '',
        minWidth: 150
      },
      {
        label_of_column: 'Used',
        header_of_column: 'USED',
        name_of_db_column: 'used',
        filterVariant: 'select',
        filterOptions: [
          {label: 'All', value: ''},
          {label: 'Yes', value: 'Yes'},
          {label: 'No', value: 'No'}
        ],
        render: (row) => (row.used ? 'Yes' : 'No'),
        minWidth: 150
      },
      {
        label_of_column: 'Expiry Date',
        header_of_column: 'EXPIRY DATE',
        name_of_db_column: 'expiryDate',
        filterVariant: 'dateRange',
        minWidth: 150
      },
      {
        label_of_column: 'Reference No',
        header_of_column: 'REFERENCE NO',
        name_of_db_column: 'referenceNo',
        minWidth: 150
      }
    ];

    // Only add actions column if not in review mode
    if (!inReview) {
      baseColumns.push({
        label_of_column: '',
        header_of_column: '',
        name_of_db_column: 'actions',
        filterable: false,
        minWidth: 60,
        render: (row) => {
          const isUsed = row.used === true || row.used === 'Yes';
          const disabled = !!row.customerId || isUsed;
          return (
            <ButtonIcon
              icon='delete'
              isDark
              type='default'
              onClick={() => handleSingleDeleteClick(row)}
              disabled={disabled}
              data-testid={`delete-${row.codeId}`}
            />
          );
        }
      });
    }

    return baseColumns;
  }, [handleSingleDeleteClick, inReview]);

  const toolbarActions = inReview
    ? []
    : [
        {
          id: 'download-template',
          label: 'Download Template',
          onClick: handleDownloadTemplate,
          icon: <Icon icon='download' size='small' />,
          variant: 'tertiary',
          isDark: true,
          requiresSelection: false,
          disabled: !showDownloadEnabled,
          size: 'small'
        },
        {
          id: 'upload-codes',
          label: 'Upload Codes',
          onClick: () => setOpenUpload(true),
          variant: 'secondary',
          isDark: true,
          requiresSelection: false,
          disabled: !showUploadEnabled,
          size: 'small'
        },
        {
          id: 'delete-codes',
          label: 'Delete Codes',
          onClick: () => handleBulkDeleteClick(),
          variant: 'secondary',
          isDark: true,
          requiresSelection: false,
          disabled: !canBulkDelete,
          size: 'small'
        }
      ];

  // Use centralised save-draft handler from parent (CreateReward/EditReward)
  // which handles duplicate-name modal and allows opting out of navigation
  const handleDraftClick = () => onSaveDraft?.({redirectOnSuccess: false});

  // no explicit cancel handler needed; ConfirmationModal's setShowModal handles close

  return (
    <div
      className={`flex flex-col rounded-lg ${inReview ? 'p-0' : 'p-6'} ${styles.rewardSetup__card}`}>
      {!inReview && (
        <LabelWithTooltip
          className='font-weight-bold text-[16px] font-bold text-[#ffffff]'
          label={'Digital Codes'}
        />
      )}

      {!rewardId && !inReview && (
        <DuplicateAlert
          type='warning'
          message={{
            subTitle:
              "Please save the reward as 'Save as Draft'. The Upload Digital Code button will be enabled once the reward is saved.",
            buttonText: 'Save as Draft'
          }}
          show={true}
          handleClick={handleDraftClick}
          iconTrailing='arrow_right_alt'
        />
      )}

      <div className={styles.rewardSetup__digitalCodesDataTable}>
        <PeacockDataTable
          key={tableKey}
          columns={columns}
          records={pagedRecords}
          isLoading={loading}
          pageSize={pageSize}
          totalRecords={totalRecords}
          paginationConfig={{
            pageIndex,
            setPageIndex,
            hidePagination: totalRecords === 0
          }}
          sortingConfig={{
            handleSorting,
            initialSortConfig: sortingObj
          }}
          filterConfig={{
            setSearchArry: setSearchObj,
            onResetAll,
            hideFilterRow: inReview || (totalRecords === 0 && !hasActiveFilters)
          }}
          selectionConfig={{
            onRowSelect,
            hideCheckbox: inReview
          }}
          toolbarConfig={{
            toolbarActions,
            hideToolbar: inReview
          }}
          displayConfig={{
            noDataTitle: 'No data available to display.'
          }}
        />
      </div>
      <div className='flex justify-end'>
        {totalRecords > 0 && (
          <LabelWithTooltip
            className='font-regular font-weight-normal text-[14px] text-[#efefef]'
            label={`${totalRecords} total item${totalRecords === 1 ? '' : 's'}`}
          />
        )}
      </div>

      {!inReview && (
        <>
          <FileUploadWithModal
            title={UPLOAD_TITLES.UPLOAD_DIGITAL_CODES_LIST}
            fileType='csv'
            uploadFor={Constants.BulkDigitalCodes}
            openUpload={openUpload}
            uploading={uploading}
            setUploading={setUploading}
            uploadProgress={uploadProgress}
            uploadErrorMessage={uploadErrorMessage}
            setOpenUpload={setOpenUpload}
            setFormData={setFormData}
            formData={formData}
            customErrorMessages={{
              wrongFormat: UPLOAD_ERROR_MESSAGES.WRONG_FORMAT
            }}
            showDownloadTemplate={true}
            handleDownloadTemplate={() =>
              handleDownloadTemplate(Constants.BulkDigitalCodes)
            }
            customRules={digitalCodesCustomRules}
            isCsvValidationRequired={true}
            primaryButtonText='Upload'
            displayOkButton={true}
            onSecondaryOk={onResetAll}
            subTitle=' '
            isPending={false}
            onClickPrimary={async () => {
              const isSuccess = await handleBulkUpload(
                formData,
                Constants.BulkDigitalCodes
              );
              if (isSuccess) {
                onResetAll();
              }
            }}
            primaryButtonDisabled={!formData?.storeList}
            handleCloseNotification={handleCloseNotification}
            handleCloseModal={handleCloseModal}
          />

          <ConfirmationModal
            showModal={confirmOpen}
            setShowModal={setConfirmOpen}
            handleConfirm={handleConfirmDelete}
            title={'Are you sure you want to delete this digital code list?'}
            text={
              'This action cannot be undone. The entire list of digital codes will be permanently removed.'
            }
            primaryButtonText='Delete'
            secondaryButtonText='Cancel'
          />
        </>
      )}
    </div>
  );
};

DigitalCodesCard.propTypes = {
  rewardStatus: PropTypes.string,
  onSaveDraft: PropTypes.func,
  inReview: PropTypes.bool
};

export default DigitalCodesCard;
